import { Button } from 'components/atoms';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPause, PiPlay } from 'react-icons/pi';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface TimeRangeSelectorProps {
  totalDuration: number;
  onChange: (start: number, end: number) => void;
  minDuration?: number;
  maxDuration?: number;
  name?: string;
  label?: string;
  audioUrl?: string;
  initialStart?: number;
  initialEnd?: number;
}

const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const TimeRangeSelector = ({
  totalDuration,
  onChange,
  minDuration = 5,
  maxDuration = 60,
  name = 'timeRange',
  label,
  audioUrl,
  initialStart = 0,
  initialEnd = maxDuration,
}: TimeRangeSelectorProps) => {
  const { t } = useTranslation();
  const [startTime, setStartTime] = useState(initialStart);
  const [endTime, setEndTime] = useState(initialEnd);
  const [isDragging, setIsDragging] = useState<
    'start' | 'end' | 'range' | null
  >(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastChangeRef = useRef({ start: initialStart, end: initialEnd });

  useEffect(() => {
    if (audioUrl) {
      audioRef.current = new Audio(audioUrl);
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    setStartTime(initialStart);
    setEndTime(initialEnd);
    lastChangeRef.current = { start: initialStart, end: initialEnd };
  }, [initialStart, initialEnd]);

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.currentTime = startTime;
      audioRef.current.play();
      setIsPlaying(true);

      audioRef.current.ontimeupdate = () => {
        if (audioRef.current && audioRef.current.currentTime >= endTime) {
          audioRef.current.pause();
          audioRef.current.currentTime = startTime;
          setIsPlaying(false);
        }
      };
    }
  }, [isPlaying, startTime, endTime]);

  const handleTimeChange = useCallback(
    (start: number, end: number) => {
      if (
        start !== lastChangeRef.current.start ||
        end !== lastChangeRef.current.end
      ) {
        lastChangeRef.current = { start, end };
        onChange(start, end);
      }
    },
    [onChange]
  );

  const updateTime = useCallback(
    (newStart: number, newEnd: number) => {
      setStartTime(newStart);
      setEndTime(newEnd);
      handleTimeChange(newStart, newEnd);
    },
    [handleTimeChange]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, type: 'start' | 'end' | 'range') => {
      e.preventDefault();
      if (!sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const position = (e.clientX - rect.left) / rect.width;
      const timeAtPosition = Math.round(position * totalDuration);

      if (type === 'range') {
        setDragOffset(timeAtPosition - startTime);
      }

      setIsDragging(type);
    },
    [startTime, totalDuration]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const position = (e.clientX - rect.left) / rect.width;
      const newTime = Math.round(position * totalDuration);

      if (isDragging === 'start') {
        let newStart = Math.max(0, newTime);
        if (endTime - newStart > maxDuration) {
          newStart = endTime - maxDuration;
        }
        if (endTime - newStart < minDuration) {
          newStart = endTime - minDuration;
        }
        updateTime(newStart, endTime);
      } else if (isDragging === 'end') {
        let newEnd = Math.min(totalDuration, newTime);
        if (newEnd - startTime > maxDuration) {
          newEnd = startTime + maxDuration;
        }
        if (newEnd - startTime < minDuration) {
          newEnd = startTime + minDuration;
        }
        updateTime(startTime, newEnd);
      } else if (isDragging === 'range') {
        const currentDuration = endTime - startTime;
        let newStart = Math.max(
          0,
          Math.min(newTime - dragOffset, totalDuration - currentDuration)
        );
        let newEnd = newStart + currentDuration;

        if (newEnd > totalDuration) {
          newEnd = totalDuration;
          newStart = newEnd - currentDuration;
        }

        updateTime(newStart, newEnd);
      }
    },
    [
      isDragging,
      totalDuration,
      startTime,
      endTime,
      maxDuration,
      minDuration,
      dragOffset,
      updateTime,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
    setDragOffset(0);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className='flex flex-col gap-4 p-4 rounded-lg'>
      {label && <label className='text-sm font-medium'>{label}</label>}

      <div className='flex flex-col gap-2'>
        <div className='flex items-center justify-between gap-4'>
          <div className='text-[15px] font-medium'>{formatTime(startTime)}</div>

          <div className='flex-1 text-center'>
            <div className='text-sm text-gray-500'>
              {t('input.label.selectedDuration')}
            </div>
            <div className='text-[15px] font-medium'>
              {formatTime(endTime - startTime)}
            </div>
          </div>

          <div className='text-[15px] font-medium'>{formatTime(endTime)}</div>
        </div>

        <div
          ref={sliderRef}
          className='relative w-full h-1 bg-gray-200 rounded-full cursor-pointer'>
          <div className='absolute h-2 bg-gray-light inset-0 rounded-full' />

          <div
            className='absolute h-2 rounded-full'
            style={{
              left: `${(startTime / totalDuration) * 100}%`,
              width: `${((endTime - startTime) / totalDuration) * 100}%`,
              backgroundColor: 'var(--primary)',
            }}
            onMouseDown={e => handleMouseDown(e, 'range')}
          />

          <div
            className='absolute w-4 h-4 rounded-full -top-1 cursor-ew-resize'
            style={{
              left: `calc(${(startTime / totalDuration) * 100}% - 8px)`,
              backgroundColor: 'var(--primary)',
            }}
            onMouseDown={e => handleMouseDown(e, 'start')}
          />

          <div
            className='absolute w-4 h-4 rounded-full -top-1 cursor-ew-resize'
            style={{
              left: `calc(${(endTime / totalDuration) * 100}% - 8px)`,
              backgroundColor: 'var(--primary)',
            }}
            onMouseDown={e => handleMouseDown(e, 'end')}
          />
        </div>

        <div className='flex justify-between text-sm text-gray-500'>
          <span>{formatTime(0)}</span>
          <span>{formatTime(totalDuration)}</span>
        </div>

        {audioUrl && (
          <div className='flex flex-col gap-2'>
            <div className='flex justify-center'>
              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={handlePlayPause}
                onlyIcon>
                <Button.Description
                  description={t(isPlaying ? 'button.pause' : 'button.play')}
                />
                <Button.Icon icon={isPlaying ? <PiPause /> : <PiPlay />} />
              </Button>
            </div>
          </div>
        )}
        <div className='text-sm text-gray text-center max-w-[300px] mx-auto'>
          {t('timestamp.description')}
        </div>
      </div>
    </div>
  );
};

export default TimeRangeSelector;
